import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/api';
import type { PositionRule } from '../../types';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { ChevronRightIcon, EmptyStateIcon } from '../../components/ui/Icons';

// COMPONENT_TOGGLE: To disable this page/feature, wrap the content of valid returns in a fragment or null, 
// or simpler, just comment out the route in App.tsx. 
// The logic below handles data fetching which can be ignored if the component is not mounted.

const PriceAlerts: React.FC = () => {
    const [rules, setRules] = useState<PositionRule[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchRules();
    }, []);

    const fetchRules = async () => {
        try {
            setLoading(true);
            const response = await apiService.positionRules.list();
            if (response.success && response.data) {
                setRules(response.data);
            } else {
                // Fallback to empty if fails or no data
                setRules([]);
                if (response.error) console.error("Failed to fetch rules:", response.error);
            }
        } catch (err) {
            console.error("Error fetching rules:", err);
            setError("Failed to load alerts. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const formatTrigger = (rule: PositionRule) => {
        const type = rule.trigger_type === 'TRAILING_STOP_LOSS' ? 'Trailing Stop' : 'Fixed Stop';
        const unit = rule.trigger_type === 'TRAILING_STOP_LOSS' ? '%' : '₹';
        return `${type} (${rule.trigger_value}${unit})`;
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex justify-center">
                <LoadingSpinner />
            </div>
        );
    }

    // FEATURE_BLOCK_START: Price Alerts List
    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
            {/* Breadcrumbs */}
            <nav className="flex items-center space-x-2 text-sm text-slate-500 mb-6">
                <Link to="/dashboard" className="hover:text-trade-primary transition-colors">Manage</Link>
                <ChevronRightIcon className="w-4 h-4" />
                <span className="text-slate-900 font-medium">Price Alerts (PriceGuard)</span>
            </nav>

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Price Alerts</h1>
                    <p className="text-slate-600 mt-2 text-lg">
                        Global view of all your active PriceGuard rules.
                    </p>
                </div>
                {/* 
                   Note: Creating new rules is best done from the Holdings page where context (stock/exchange) is available. 
                   We direct users there for creation.
                */}
                <Link to="/holdings" className="btn btn-primary shadow-sm hover:shadow-md transition-all">
                    Go to Holdings to Add Rules
                </Link>
            </div>

            {error && (
                <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6 border border-red-200">
                    {error}
                </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                        <thead className="bg-slate-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Symbol</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Rule Name</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Condition</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Created</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-200">
                            {rules.map((rule) => (
                                <tr key={rule.id} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="font-bold text-slate-900">{rule.symbol}</div>
                                        <div className="text-xs text-slate-500">{rule.exchange}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-slate-900">{rule.rule_name}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                            {formatTrigger(rule)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${rule.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                            }`}>
                                            {rule.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                                        {formatDate(rule.created_at)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {rules.length === 0 && !error && (
                    <div className="text-center py-12">
                        <EmptyStateIcon className="mx-auto h-12 w-12 text-slate-400" />
                        <p className="mt-2 text-slate-500">No active rules found.</p>
                        <p className="text-sm text-slate-400">Head to Holdings to set up safety nets for your positions.</p>
                    </div>
                )}
            </div>
        </div>
    );
    // FEATURE_BLOCK_END
};

export default PriceAlerts;
